import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunks
export const blockUser = createAsyncThunk(
  'block/blockUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.blockUser(userId);
      return { userId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to block user');
    }
  }
);

export const unblockUser = createAsyncThunk(
  'block/unblockUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.unblockUser(userId);
      return { userId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unblock user');
    }
  }
);

export const fetchBlockedUsers = createAsyncThunk(
  'block/fetchBlockedUsers',
  async ({ limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getBlockedUsers({ limit, offset });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch blocked users');
    }
  }
);

export const checkBlockStatus = createAsyncThunk(
  'block/checkBlockStatus',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.checkBlockStatus(userId);
      return { userId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to check block status');
    }
  }
);

const blockSlice = createSlice({
  name: 'block',
  initialState: {
    blockedUsers: [],
    blockStatuses: {}, // { userId: { isBlocked: boolean, isBlockedBy: boolean } }
    loading: false,
    error: null,
    hasMore: true,
  },
  reducers: {
    // Обновление статуса блокировки (приходит через WebSocket)
    updateBlockStatus: (state, action) => {
      const { userId, isBlocked, isBlockedBy } = action.payload;
      state.blockStatuses[userId] = { isBlocked, isBlockedBy };
    },

    // Добавление заблокированного пользователя
    addBlockedUser: (state, action) => {
      const user = action.payload;
      const exists = state.blockedUsers.find(u => u.id === user.id);
      if (!exists) {
        state.blockedUsers.push(user);
        state.blockStatuses[user.id] = { isBlocked: true, isBlockedBy: false };
      }
    },

    // Удаление заблокированного пользователя
    removeBlockedUser: (state, action) => {
      const { userId } = action.payload;
      state.blockedUsers = state.blockedUsers.filter(u => u.id !== userId);
      if (state.blockStatuses[userId]) {
        state.blockStatuses[userId] = { isBlocked: false, isBlockedBy: false };
      }
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetBlockState: (state) => {
      state.blockedUsers = [];
      state.blockStatuses = {};
      state.loading = false;
      state.error = null;
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Блокировка пользователя
      .addCase(blockUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        state.loading = false;
        const { userId } = action.payload;
        state.blockStatuses[userId] = { isBlocked: true, isBlockedBy: false };
      })
      .addCase(blockUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Разблокировка пользователя
      .addCase(unblockUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unblockUser.fulfilled, (state, action) => {
        state.loading = false;
        // action.payload содержит { userId, ...response.data }
        const userId = action.payload?.userId || action.meta.arg;
        if (userId) {
          state.blockedUsers = state.blockedUsers.filter(u => u.id !== userId);
          if (state.blockStatuses[userId]) {
            state.blockStatuses[userId] = { isBlocked: false, isBlockedBy: false };
          }
        }
      })
      .addCase(unblockUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Получение списка заблокированных
      .addCase(fetchBlockedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlockedUsers.fulfilled, (state, action) => {
        state.loading = false;
        const { blocked_users, has_more } = action.payload;
        
        // Если это первая загрузка, заменяем список
        if (state.blockedUsers.length === 0) {
          state.blockedUsers = blocked_users || [];
        } else {
          // Иначе добавляем новых пользователей
          const existingIds = new Set(state.blockedUsers.map(u => u.id));
          const newUsers = (blocked_users || []).filter(u => !existingIds.has(u.id));
          state.blockedUsers = [...state.blockedUsers, ...newUsers];
        }
        
        state.hasMore = has_more !== false;
        
        // Обновляем статусы блокировки
        (blocked_users || []).forEach(user => {
          state.blockStatuses[user.id] = { isBlocked: true, isBlockedBy: false };
        });
      })
      .addCase(fetchBlockedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Проверка статуса блокировки
      .addCase(checkBlockStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkBlockStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, is_blocked, is_blocked_by } = action.payload;
        state.blockStatuses[userId] = {
          isBlocked: is_blocked || false,
          isBlockedBy: is_blocked_by || false,
        };
      })
      .addCase(checkBlockStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Экспортируем actions
export const {
  updateBlockStatus,
  addBlockedUser,
  removeBlockedUser,
  clearError,
  resetBlockState,
} = blockSlice.actions;

// Селекторы
export const selectBlockedUsers = (state) => state.block.blockedUsers;
export const selectBlockStatus = (state, userId) => 
  state.block.blockStatuses[userId] || { isBlocked: false, isBlockedBy: false };
export const selectIsBlocked = (state, userId) => 
  state.block.blockStatuses[userId]?.isBlocked || false;
export const selectIsBlockedBy = (state, userId) => 
  state.block.blockStatuses[userId]?.isBlockedBy || false;
export const selectBlockLoading = (state) => state.block.loading;
export const selectBlockError = (state) => state.block.error;
export const selectHasMoreBlocked = (state) => state.block.hasMore;

export default blockSlice.reducer;

